import { useEffect, useState } from 'react';
import { supabase } from '../services/supabaseClient';
import { useAuth } from '../context/AuthContext';

export type Plan = 'free' | 'trial' | 'pro';
export type SubscriptionStatus = 'active' | 'past_due' | 'cancelled';

export interface SubscriptionState {
  plan: Plan;
  status: SubscriptionStatus;
  trialEndsAt: Date | null;
  trialDaysLeft: number;   // -1 when not in an active trial
  isPro: boolean;          // true for 'pro' OR active 'trial' — use this for feature gates
  isTrial: boolean;        // true only during the 7-day trial window
  isPastDue: boolean;      // payment failed; show "fix payment" banner, NOT upgrade prompt
  couponApplied: boolean;  // true for one render cycle after ?coupon_applied=1 redirect
  isLoading: boolean;
}

const DEFAULT_STATE: SubscriptionState = {
  plan: 'free',
  status: 'active',
  trialEndsAt: null,
  trialDaysLeft: -1,
  isPro: false,
  isTrial: false,
  isPastDue: false,
  couponApplied: false,
  isLoading: true,
};

export function useSubscription(): SubscriptionState {
  const { user } = useAuth();
  const [state, setState] = useState<SubscriptionState>(DEFAULT_STATE);
  const [refreshKey, setRefreshKey] = useState(0);

  // On return from Stripe checkout or coupon redemption, force a re-fetch and clean the URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('subscribed')) {
      setRefreshKey(k => k + 1);
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.has('coupon_applied')) {
      setState(s => ({ ...s, couponApplied: true }));
      setRefreshKey(k => k + 1);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setState({ ...DEFAULT_STATE, isLoading: false });
      return;
    }

    let cancelled = false;

    const fetch = () => {
      supabase
        .from('user_subscriptions')
        .select('plan, status, trial_ends_at, subscription_ends_at, cancel_at_period_end, payment_failed_at')
        .eq('user_id', user.id)
        .maybeSingle()
        .then(({ data, error }) => {
          if (cancelled) return;

          if (error || !data) {
            setState({ ...DEFAULT_STATE, isPro: true, isTrial: true, trialDaysLeft: 7, isLoading: false });
            return;
          }

          const now = new Date();
          const trialEndsAt = data.trial_ends_at ? new Date(data.trial_ends_at) : null;
          const trialActive = data.plan === 'trial' && trialEndsAt != null && trialEndsAt > now;
          const trialDaysLeft = trialActive
            ? Math.ceil((trialEndsAt!.getTime() - now.getTime()) / 86_400_000)
            : -1;

          setState(s => ({
            ...s,
            plan: data.plan as Plan,
            status: data.status as SubscriptionStatus,
            trialEndsAt,
            trialDaysLeft,
            isPro: data.plan === 'pro' || trialActive,
            isTrial: trialActive,
            isPastDue: data.status === 'past_due',
            isLoading: false,
          }));
        });
    };

    // When coming back from Stripe, the webhook may not have fired yet — retry a few times
    if (refreshKey > 0) {
      setState(s => ({ ...s, isLoading: true }));
      const delays = [1000, 3000, 5000];
      const timers = delays.map(d => setTimeout(fetch, d));
      return () => { cancelled = true; timers.forEach(clearTimeout); };
    }

    fetch();
    return () => { cancelled = true; };
  }, [user, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return state;
}
